import { reactive, computed } from 'vue'
import zh from '../locales/zh.json'
import en from '../locales/en.json'

const STORAGE_KEY = 'yog-lang'
const DEFAULT_LOCALE = 'zh'
const messages = { zh, en }

const supportedLocales = Object.keys(messages)

function loadLocale() {
  if (typeof window === 'undefined') {
    return DEFAULT_LOCALE
  }
  const saved = window.localStorage.getItem(STORAGE_KEY)
  if (saved && supportedLocales.includes(saved)) {
    return saved
  }
  return DEFAULT_LOCALE
}

const state = reactive({
  locale: loadLocale()
})

function persistLocale(locale) {
  if (typeof window === 'undefined') {
    return
  }
  window.localStorage.setItem(STORAGE_KEY, locale)
}

function resolveMessage(locale, key) {
  return key.split('.').reduce((acc, segment) => {
    if (acc && typeof acc === 'object' && segment in acc) {
      return acc[segment]
    }
    return undefined
  }, messages[locale])
}

function interpolate(template, params) {
  if (!params || typeof template !== 'string') {
    return template
  }
  return Object.keys(params).reduce((str, param) => {
    return str.replace(new RegExp(`{{\\s*${param}\\s*}}`, 'g'), params[param])
  }, template)
}

function translate(key, params, fallback) {
  const currentLocale = state.locale
  let message = resolveMessage(currentLocale, key)
  if (message === undefined) {
    const fallbackLocale = messages[DEFAULT_LOCALE] ? DEFAULT_LOCALE : currentLocale
    message = resolveMessage(fallbackLocale, key)
  }
  if (message === undefined) {
    message = fallback || key
  }
  return interpolate(message, params)
}

function setLocale(locale) {
  if (!supportedLocales.includes(locale)) {
    locale = DEFAULT_LOCALE
  }
  if (state.locale !== locale) {
    state.locale = locale
    persistLocale(locale)
  }
}

function getLocale() {
  return state.locale
}

const y18n = {
  t: translate,
  setLocale,
  getLocale,
  locale: computed(() => state.locale),
  availableLocales: supportedLocales
}

export function useY18n() {
  return {
    ...y18n
  }
}

export const y18nPlugin = {
  install(app) {
    app.config.globalProperties.$t = translate
    app.config.globalProperties.$setLang = setLocale
    app.config.globalProperties.$getLang = getLocale
    app.config.globalProperties.$y18n = y18n
    app.provide('y18n', y18n)
  }
}

export default y18n
